import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Button, Badge, ListGroup, Breadcrumb, Spinner, Alert, Modal } from 'react-bootstrap';
import { Pencil, Trash, Download, FileEarmarkPlus, ArrowLeft } from 'react-bootstrap-icons';
import useApi from '../hooks/useApi';
import { useDocumentContext } from '../contexts/DocumentContext';

const DocumentDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const api = useApi();
    const apiRef = useRef(api);
    const { documentTypes } = useDocumentContext();

    const [document, setDocument] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Keep apiRef updated with latest api instance
    useEffect(() => {
        apiRef.current = api;
    }, [api]);

    useEffect(() => {
        let isMounted = true;

        const fetchDocument = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await apiRef.current.get(`/api/v1/documents/${id}`);
                if (isMounted) {
                    setDocument(response.data);
                }
            } catch (err) {
                console.error('Failed to fetch document:', err);
                if (isMounted) {
                    if (err.response?.status === 404) {
                        setError('Document not found');
                    } else {
                        setError('Failed to load document');
                    }
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchDocument();

        return () => {
            isMounted = false;
        };
    }, [id]); // Only re-fetch when document ID changes

    const getTypeName = (typeId) => {
        const type = documentTypes.find(t => t.id === typeId);
        return type ? type.name : 'Unknown';
    };

    const getExpiryStatus = (expiryDate) => {
        if (!expiryDate) return null;

        const today = new Date();
        const expiry = new Date(expiryDate);
        const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

        if (daysUntilExpiry < 0) {
            return { variant: 'danger', text: 'Expired' };
        } else if (daysUntilExpiry <= 30) {
            return { variant: 'warning', text: `Expires in ${daysUntilExpiry} days` };
        }
        return { variant: 'success', text: 'Valid' };
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '-';
        const units = ['B', 'KB', 'MB', 'GB'];
        let size = bytes;
        let unitIndex = 0;
        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024;
            unitIndex++;
        }
        return `${size.toFixed(1)} ${units[unitIndex]}`;
    };

    const handleEdit = () => {
        console.log('Edit document:', id);
        // TODO: Navigate to edit page or open edit modal
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await api.delete(`/api/v1/documents/${id}`);
            setShowDeleteModal(false);
            navigate('/documents/list');
        } catch (err) {
            console.error('Failed to delete document:', err);
        } finally {
            setDeleting(false);
        }
    };

    const handleDownload = async (file) => {
        try {
            const response = await api.get(`/api/v1/documents/${id}/files/${file.id}/download`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = window.document.createElement('a');
            link.href = url;
            link.setAttribute('download', file.name);
            window.document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download file:', err);
        }
    };

    const handleAttachFiles = () => {
        console.log('Attach files to document:', id);
        // TODO: Open file upload modal
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger">
                <Alert.Heading>{error === 'Document not found' ? '404 - Not Found' : 'Error'}</Alert.Heading>
                <p>{error}</p>
                <Button variant="outline-danger" onClick={() => navigate('/documents/list')}>
                    <ArrowLeft className="me-2" />
                    Back to Documents
                </Button>
            </Alert>
        );
    }

    if (!document) {
        return null;
    }

    const expiryStatus = getExpiryStatus(document.expiryDate || document.expiry_date);
    const files = document.files || [];

    return (
        <div className="document-detail">
            <Breadcrumb className="mb-3">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/' }}>Home</Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/documents/list' }}>Documents</Breadcrumb.Item>
                <Breadcrumb.Item active>{document.title}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>{document.title}</h2>
                <div className="d-flex gap-2">
                    <Button variant="primary" onClick={handleAttachFiles}>
                        <FileEarmarkPlus className="me-2" />
                        Attach Files
                    </Button>
                    <Button variant="warning" onClick={handleEdit}>
                        <Pencil className="me-2" />
                        Edit
                    </Button>
                    <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
                        <Trash className="me-2" />
                        Delete
                    </Button>
                </div>
            </div>

            <Card className="mb-4">
                <Card.Header>Document Information</Card.Header>
                <Card.Body>
                    <div className="row">
                        <div className="col-md-6">
                            <dl className="row mb-0">
                                <dt className="col-sm-4">Title</dt>
                                <dd className="col-sm-8">{document.title}</dd>

                                <dt className="col-sm-4">Type</dt>
                                <dd className="col-sm-8">
                                    {getTypeName(document.documentTypeId || document.documentTypeID)}
                                </dd>

                                <dt className="col-sm-4">Expiry Date</dt>
                                <dd className="col-sm-8">
                                    {formatDate(document.expiryDate || document.expiry_date)}
                                    {expiryStatus && (
                                        <Badge bg={expiryStatus.variant} className="ms-2">
                                            {expiryStatus.text}
                                        </Badge>
                                    )}
                                </dd>

                                <dt className="col-sm-4">Location</dt>
                                <dd className="col-sm-8">
                                    {document.treeNodeName || document.treePath || '-'}
                                </dd>
                            </dl>
                        </div>
                        <div className="col-md-6">
                            <dl className="row mb-0">
                                <dt className="col-sm-4">Created</dt>
                                <dd className="col-sm-8">
                                    {formatDateTime(document.createdAt || document.created_at)}
                                </dd>

                                <dt className="col-sm-4">Modified</dt>
                                <dd className="col-sm-8">
                                    {formatDateTime(document.updatedAt || document.updated_at)}
                                </dd>

                                <dt className="col-sm-4">ID</dt>
                                <dd className="col-sm-8">
                                    <code>{document.id}</code>
                                </dd>
                            </dl>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>Attached Files ({files.length})</span>
                </Card.Header>
                {files.length > 0 ? (
                    <ListGroup variant="flush">
                        {files.map((file) => (
                            <ListGroup.Item
                                key={file.id}
                                className="d-flex justify-content-between align-items-center"
                            >
                                <div>
                                    <strong>{file.name}</strong>
                                    <br />
                                    <small className="text-muted">
                                        {formatFileSize(file.size)} | Uploaded: {formatDate(file.uploadedAt || file.uploaded_at)}
                                    </small>
                                </div>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleDownload(file)}
                                >
                                    <Download className="me-1" />
                                    Download
                                </Button>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                ) : (
                    <Card.Body className="text-muted text-center">
                        No files attached to this document.
                    </Card.Body>
                )}
            </Card>

            <div className="mt-3">
                <Button variant="outline-secondary" onClick={() => navigate('/documents/list')}>
                    <ArrowLeft className="me-2" />
                    Back to Documents
                </Button>
            </div>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete "{document.title}"? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DocumentDetail;
